import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { Subject } from 'rxjs';
import { SNACK_BAR_CONFIG } from './snack-bar-config';
import { ssClassMerge } from '../utils/ss-class-merge';

@Component({
  selector: 'ss-snack-bar',
  imports: [],
  templateUrl: './snack-bar.component.html',
  styleUrl: './snack-bar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SnackBarComponent {
  private destroyRef = inject(DestroyRef);
  private readonly config = inject(SNACK_BAR_CONFIG, { optional: true });

  readonly textClasses = this.config?.textClasses ?? '';
  readonly containerClasses = this.config?.baseContainerClasses ?? '';
  readonly successContainerClasses = this.config?.successContainerClasses ?? '';
  readonly errorContainerClasses = this.config?.errorContainerClasses ?? '';

  title = input<string>('');
  message = input.required<string>();
  type = input<'success' | 'error'>('success');
  closeRequested = output<void>();
  destroy$ = new Subject<boolean>();

  // isVisible is primarily used to track the enter/leave animations as this enters/leaves the dom.
  // Using Angular's new enter/leave animations in place of old Angular animations: https://angular.dev/guide/animations#element-removal-order-matters
  isVisible = signal(true);
  private readonly baseClasses = signal(
    'p-4 bg-white text-black rounded-lg flex items-center w-full max-w-80 shadow-2xl snack-container'
  );

  readonly computedClasses = computed(() =>
    ssClassMerge(
      this.baseClasses(),
      this.containerClasses,
      this.type() === 'success'
        ? this.successContainerClasses
        : this.errorContainerClasses
    )
  );

  private cleanup = this.destroyRef.onDestroy(() => {
    this.destroy$.next(true);
    this.destroy$.complete();
  });

  close(): void {
    if (!this.isVisible()) return;
    this.isVisible.set(false);
  }
}
